import React from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Alert, AlertDescription } from "../ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Input } from "../ui/input";
import { toast } from "sonner";
import {
  Download, Upload, FolderDown, FolderUp, File, Archive, RefreshCw, Database,
  Loader2, CheckCircle2, AlertTriangle, Save, Clock, Trash2, History, FolderOpen,
  Briefcase, Building2, FileText, Users, Mail, FormInput, Copy, Settings, Plus,
  Cable, Play, Server
} from "lucide-react";

// collections list is now passed dynamically via AVAILABLE_COLLECTIONS prop

const BackupRestoreCard = React.memo(function BackupRestoreCard({
  selectiveExportDialogOpen, setSelectiveExportDialogOpen,
  selectiveImportDialogOpen, setSelectiveImportDialogOpen,
  syncDatabaseDialogOpen, setSyncDatabaseDialogOpen,
  exportingAll, setExportingAll,
  exportingEnv, setExportingEnv,
  importAllDialogOpen, setImportAllDialogOpen,
  importingAll, setImportingAll,
  importAllFile, setImportAllFile,
  importAllResult, setImportAllResult,
  importProgress,
  showImportWarning, setShowImportWarning,
  exportResultDialogOpen, setExportResultDialogOpen,
  exportResultData, setExportResultData,
  importFormsDialogOpen, setImportFormsDialogOpen,
  importingForms, setImportingForms,
  importFormsFile, setImportFormsFile,
  importFormsResult, setImportFormsResult,
  exportingForms, setExportingForms,
  exportDialogOpen, setExportDialogOpen,
  importDialogOpen, setImportDialogOpen,
  exporting, setExporting,
  importing, setImporting,
  selectedImportFiles, setSelectedImportFiles,
  importResult, setImportResult,
  combinedBackupFile, setCombinedBackupFile,
  selectedFolderName, setSelectedFolderName,
  folderFiles, setFolderFiles,
  combinedFileInputRef,
  crmExportDialogOpen, setCrmExportDialogOpen,
  crmImportDialogOpen, setCrmImportDialogOpen,
  crmExporting, setCrmExporting,
  crmImporting, setCrmImporting,
  crmBackupFile, setCrmBackupFile,
  crmImportResult, setCrmImportResult,
  crmFileInputRef,
  backupSettings, setBackupSettings,
  backupSettingsLoading,
  savingBackupSettings,
  backupList,
  runningBackup,
  handleSaveBackupSettings, handleRunBackupNow, handleDownloadBackup, handleDeleteBackup,
  fetchBackupSettings,
  formatFileSize,
  handleExportAllData, handleExportEnvData, handleExportForms,
  handleImportAllData, handleImportPreview, importPreview, setImportPreview, loadingPreview,
  handleImportFormsData,
  handleExportCombined, handleExportAll, handleExportTable,
  handleImportCombined, handleImportFiles,
  handleFileSelect, clearSelectedFile,
  handleCombinedFileSelect, handleSelectFolder,
  handleExportCRM, handleImportCRM,
  selectedCollections, setSelectedCollections,
  exportingSelected, setExportingSelected,
  importingSelected, setImportingSelected,
  selectiveImportFile, setSelectiveImportFile,
  selectiveImportResult, setSelectiveImportResult,
  selectiveImportFileRef,
  handleSelectiveExport, handleSelectiveImport,
  toggleCollection, selectAllCollections, deselectAllCollections,
  AVAILABLE_COLLECTIONS,
  syncMode, setSyncMode,
  syncConnectionString, setSyncConnectionString,
  syncDatabaseName, setSyncDatabaseName,
  testingSyncConnection, setTestingSyncConnection,
  syncConnectionTestResult, setSyncConnectionTestResult,
  syncingDatabase, setSyncingDatabase,
  syncResult, setSyncResult,
  handleTestSyncConnection, handleSyncDatabase,
  fileInputRefs
}) {
  return (
    <>
      {/* Selective Export & Import Card */}
      <Card className="border-teal-200 bg-teal-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-teal-600" />
            <CardTitle className="text-teal-900">Selective Export & Import</CardTitle>
          </div>
          <CardDescription className="text-teal-700">
            Export or import specific collections (tables) individually. Choose exactly which data you want to backup or restore.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <Button
              onClick={() => setSelectiveExportDialogOpen(true)}
              variant="outline"
              className="flex-1 border-teal-300 text-teal-700 hover:bg-teal-100"
            >
              <Download className="mr-2 h-4 w-4" />
              Export Selected Collections
            </Button>
            <Button
              onClick={() => setSelectiveImportDialogOpen(true)}
              variant="outline"
              className="flex-1 border-teal-300 text-teal-700 hover:bg-teal-100"
            >
              <Upload className="mr-2 h-4 w-4" />
              Import Selected Collections
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sync Database */}
      <Card className="border-purple-200 bg-purple-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-purple-900">Sync Database</CardTitle>
          </div>
          <CardDescription className="text-purple-700">
            Sync data to another MongoDB database. Choose merge mode (add new fields only) or override mode (replace all data).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={() => setSyncDatabaseDialogOpen(true)}
            variant="outline"
            className="border-purple-300 text-purple-700 hover:bg-purple-100"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Open Sync Database
          </Button>
        </CardContent>
      </Card>

      {/* Complete Database Backup & Restore Card */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-blue-900">Complete Database Backup & Restore</CardTitle>
          </div>
          <CardDescription className="text-blue-700">
            Export ALL database tables (LOGS + CRM) to a single JSON file for complete backup, or export ENV configuration separately.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Export ALL Data */}
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FolderDown className="h-5 w-5 text-green-600" />
                <h3 className="font-semibold text-green-800">Export ALL Data</h3>
              </div>
              <p className="text-sm text-green-700 mb-3">
                Download complete database backup as a single JSON file.
              </p>
              <Button
                variant="outline"
                className="w-full border-green-500 text-green-700 hover:bg-green-100"
                onClick={handleExportAllData}
                disabled={exportingAll}
              >
                {exportingAll ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Exporting...</>
                ) : (
                  <><Download className="mr-2 h-4 w-4" /> Export ALL</>
                )}
              </Button>
            </div>

            {/* Import ALL Data */}
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FolderUp className="h-5 w-5 text-orange-600" />
                <h3 className="font-semibold text-orange-800">Import ALL Data</h3>
              </div>
              <p className="text-sm text-orange-700 mb-3">
                Restore complete database from a backup JSON file.
              </p>
              <Button
                variant="outline"
                className="w-full border-orange-500 text-orange-700 hover:bg-orange-100"
                onClick={() => {
                  setImportAllFile(null);
                  setImportAllResult(null);
                  setImportAllDialogOpen(true);
                }}
              >
                <Upload className="mr-2 h-4 w-4" />
                Import ALL
              </Button>
            </div>

            {/* Export ENV Data */}
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="h-5 w-5 text-purple-600" />
                <h3 className="font-semibold text-purple-800">Export ENV Config</h3>
              </div>
              <p className="text-sm text-purple-700 mb-3">
                Export environment configuration to a separate JSON file.
              </p>
              <Button
                variant="outline"
                className="w-full border-purple-500 text-purple-700 hover:bg-purple-100"
                onClick={handleExportEnvData}
                disabled={exportingEnv}
              >
                {exportingEnv ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Exporting...</>
                ) : (
                  <><Download className="mr-2 h-4 w-4" /> Export ENV</>
                )}
              </Button>
            </div>

            {/* Export FORMS */}
            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-indigo-600" />
                <h3 className="font-semibold text-indigo-800">Export FORMS</h3>
              </div>
              <p className="text-sm text-indigo-700 mb-3">
                Export all forms with field definitions to a JSON file.
              </p>
              <Button
                variant="outline"
                className="w-full border-indigo-500 text-indigo-700 hover:bg-indigo-100"
                onClick={handleExportForms}
                disabled={exportingForms}
              >
                {exportingForms ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Exporting...</>
                ) : (
                  <><Download className="mr-2 h-4 w-4" /> Export FORMS</>
                )}
              </Button>
            </div>

            {/* Import FORMS */}
            <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-cyan-600" />
                <h3 className="font-semibold text-cyan-800">Import FORMS</h3>
              </div>
              <p className="text-sm text-cyan-700 mb-3">
                Import forms from an exported forms JSON file.
              </p>
              <Button
                variant="outline"
                className="w-full border-cyan-500 text-cyan-700 hover:bg-cyan-100"
                onClick={() => {
                  setImportFormsFile(null);
                  setImportFormsResult(null);
                  setImportFormsDialogOpen(true);
                }}
              >
                <Upload className="mr-2 h-4 w-4" />
                Import FORMS
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Automatic Backups Card */}
      <Card className="border-indigo-200 bg-indigo-50/30">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-indigo-900">Automatic Backups</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-indigo-700">
                {backupSettings.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <Switch
                checked={backupSettings.enabled}
                onCheckedChange={(checked) => setBackupSettings({...backupSettings, enabled: checked})}
                data-testid="auto-backup-toggle"
              />
            </div>
          </div>
          <CardDescription className="text-indigo-700">
            Schedule automatic daily backups at 2 AM EST. Backups are stored locally and kept for {backupSettings.retention_days} days.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {backupSettingsLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
            </div>
          ) : (
            <>
              {/* Settings Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-indigo-800">Backup Frequency</Label>
                  <Select
                    value={backupSettings.frequency}
                    onValueChange={(val) => setBackupSettings({...backupSettings, frequency: val})}
                  >
                    <SelectTrigger className="mt-1 bg-white" data-testid="backup-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily (2 AM EST)</SelectItem>
                      <SelectItem value="weekly">Weekly (Sunday 2 AM EST)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-indigo-800">Retention Period</Label>
                  <Select
                    value={String(backupSettings.retention_days)}
                    onValueChange={(val) => setBackupSettings({...backupSettings, retention_days: parseInt(val)})}
                  >
                    <SelectTrigger className="mt-1 bg-white" data-testid="backup-retention">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="13">13 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    onClick={handleSaveBackupSettings}
                    disabled={savingBackupSettings}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="save-backup-settings"
                  >
                    {savingBackupSettings ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Save Settings
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleRunBackupNow}
                    disabled={runningBackup}
                    className="border-indigo-500 text-indigo-700 hover:bg-indigo-100"
                    data-testid="run-backup-now"
                  >
                    {runningBackup ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="mr-2 h-4 w-4" />
                    )}
                    Backup Now
                  </Button>
                </div>
              </div>

              {/* Backup List */}
              {backupList.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-indigo-800 flex items-center gap-2">
                      <Archive className="h-4 w-4" />
                      Stored Backups ({backupList.length})
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchBackupSettings}
                      className="text-indigo-600"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="border rounded-lg bg-white max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-indigo-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-indigo-800">Filename</th>
                          <th className="text-left px-3 py-2 font-medium text-indigo-800">Date</th>
                          <th className="text-right px-3 py-2 font-medium text-indigo-800">Size</th>
                          <th className="text-center px-3 py-2 font-medium text-indigo-800">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backupList.map((backup, idx) => (
                          <tr key={backup.filename} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="px-3 py-2 font-mono text-xs text-slate-700">{backup.filename}</td>
                            <td className="px-3 py-2 text-slate-600">
                              {new Date(backup.created_at).toLocaleString()}
                            </td>
                            <td className="px-3 py-2 text-right text-slate-600">
                              {formatFileSize(backup.file_size)}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-indigo-600 hover:text-indigo-800"
                                  onClick={() => handleDownloadBackup(backup.filename)}
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteBackup(backup.filename)}
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {backupList.length === 0 && (
                <div className="text-center py-6 text-indigo-600">
                  <Archive className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No backups yet. Click "Backup Now" to create your first backup.</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ==================== DIALOGS ==================== */}

      {/* Import ALL Dialog */}
      <Dialog open={importAllDialogOpen} onOpenChange={(open) => {
        setImportAllDialogOpen(open);
        if (!open) {
          setShowImportWarning(false);
          setImportAllFile(null);
          setImportAllResult(null);
          setImportPreview(null);
        }
      }}>
        <DialogContent className={importPreview ? "max-w-2xl max-h-[90vh] overflow-y-auto" : "max-w-md"}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <FolderUp className="h-5 w-5" />
              Import Complete Database
            </DialogTitle>
            <DialogDescription>
              Select a backup file to preview and restore all database tables.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!importPreview && !showImportWarning ? (
              /* Step 1: File Selection */
              <>
                <div className="border-2 border-dashed border-orange-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".zip,.json"
                    onChange={(e) => {
                      setImportAllFile(e.target.files[0]);
                      setImportAllResult(null);
                      setImportPreview(null);
                    }}
                    className="hidden"
                    id="import-all-file"
                  />
                  <label htmlFor="import-all-file" className="cursor-pointer">
                    <FolderUp className="h-12 w-12 text-orange-400 mx-auto mb-2" />
                    <p className="text-sm text-orange-600">
                      {importAllFile ? importAllFile.name : 'Click to select backup file (.zip or .json)'}
                    </p>
                  </label>
                </div>

                {importAllResult && (
                  <div className={`p-3 rounded-lg ${importAllResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    <div className="flex items-start gap-2">
                      {importAllResult.success ? (
                        <CheckCircle2 className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{importAllResult.success ? 'Import Successful!' : 'Import Failed'}</p>
                        <p className="text-sm mt-1 whitespace-pre-wrap break-words">{importAllResult.message}</p>
                      </div>
                    </div>
                    {importAllResult.details && (
                      <div className="mt-2 text-xs">
                        {Object.entries(importAllResult.details).map(([key, val]) => (
                          <div key={key}>{key}: {val.count || 0} records</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                  <p className="text-sm text-amber-700">
                    <strong>Note:</strong> Click "Preview Import" to see what will change before importing.
                  </p>
                </div>
              </>
            ) : importPreview && !showImportWarning ? (
              /* Step 2: Preview */
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>File:</strong> {importPreview.file_name} ({importPreview.file_size_mb} MB)
                    {importPreview.backup_date && <> &middot; <strong>Backup date:</strong> {new Date(importPreview.backup_date).toLocaleString()}</>}
                  </p>
                </div>

                {/* Collections table */}
                <div>
                  <h4 className="text-sm font-semibold mb-2">Collections ({importPreview.collections.length})</h4>
                  <div className="border rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Collection</th>
                          <th className="text-right px-3 py-2 font-medium">Current</th>
                          <th className="text-right px-3 py-2 font-medium">Backup</th>
                          <th className="text-right px-3 py-2 font-medium">Change</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {importPreview.collections.map((c) => {
                          const diff = c.backup_count - c.current_count;
                          return (
                            <tr key={c.name} className="hover:bg-gray-50">
                              <td className="px-3 py-1.5 font-mono text-xs">{c.name}</td>
                              <td className="px-3 py-1.5 text-right">{c.current_count.toLocaleString()}</td>
                              <td className="px-3 py-1.5 text-right">{c.backup_count.toLocaleString()}</td>
                              <td className={`px-3 py-1.5 text-right font-medium ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                {diff > 0 ? '+' : ''}{diff.toLocaleString()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Leads status impact */}
                {importPreview.leads_status_impact && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Lead Status Breakdown (from backup)</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium">Status</th>
                            <th className="text-right px-3 py-2 font-medium">Count in Backup</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {Object.entries(importPreview.leads_status_impact.backup_status_counts)
                            .sort(([,a], [,b]) => b - a)
                            .map(([status, count]) => (
                              <tr key={status} className="hover:bg-gray-50">
                                <td className="px-3 py-1.5">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    status === 'assigned' ? 'bg-blue-100 text-blue-700' :
                                    status === 'contacted' ? 'bg-yellow-100 text-yellow-700' :
                                    status === 'qualified' ? 'bg-green-100 text-green-700' :
                                    status === 'new' ? 'bg-gray-100 text-gray-700' :
                                    status === 'closed_won' ? 'bg-emerald-100 text-emerald-700' :
                                    status === 'closed_lost' ? 'bg-red-100 text-red-700' :
                                    status === 'inactive' ? 'bg-slate-100 text-slate-600' :
                                    'bg-slate-100 text-slate-700'
                                  }`}>{status}</span>
                                </td>
                                <td className="px-3 py-1.5 text-right">{count.toLocaleString()}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-2 bg-green-50 border border-green-200 p-2 rounded text-xs text-green-700">
                      <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" />
                      <strong>{importPreview.leads_status_impact.statuses_preserved}</strong> leads will keep their status (contacted, qualified, etc.).
                      Only <strong>{importPreview.leads_status_impact.new_promoted_to_assigned}</strong> "new" owned leads will be promoted to "assigned".
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Step 3: Warning + Progress */
              <div className="space-y-4">
                {importingAll && importProgress ? (
                  /* Live Progress View */
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Importing Database...
                        </h3>
                        <span className="text-lg font-bold text-blue-700">{importProgress.percent}%</span>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${importProgress.percent}%` }}
                        />
                      </div>

                      {/* Current Collection */}
                      <div className="flex items-center justify-between text-sm">
                        <p className="text-blue-700 font-mono truncate flex-1">
                          {importProgress.collection}
                        </p>
                        {importProgress.total > 0 && (
                          <span className="text-blue-500 ml-2 whitespace-nowrap">
                            {importProgress.index + 1} / {importProgress.total} tables
                          </span>
                        )}
                      </div>

                      {importProgress.records > 0 && (
                        <p className="text-xs text-blue-500">
                          {importProgress.records.toLocaleString()} records
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Warning Confirmation View */
                  <>
                    <div className="bg-red-50 border-2 border-red-300 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-6 w-6 text-red-600" />
                        <h3 className="font-bold text-red-800 text-lg">&#x26A0;&#xFE0F; WARNING</h3>
                      </div>
                      <p className="text-red-700 font-medium mb-2">
                        This action will PERMANENTLY DELETE all existing data and replace it with the backup file contents.
                      </p>
                      <ul className="text-sm text-red-600 list-disc ml-4 space-y-1">
                        <li>All users, logs, teams, forms will be replaced</li>
                        <li>All CRM data (companies, leads, tasks) will be replaced</li>
                        <li>This action CANNOT be undone</li>
                      </ul>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <strong>Selected file:</strong> {importAllFile?.name}
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {!importPreview && !showImportWarning ? (
              /* Step 1 footer: Cancel + Preview */
              <>
                <Button variant="outline" onClick={() => setImportAllDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleImportPreview}
                  disabled={!importAllFile || loadingPreview}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {loadingPreview ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyzing...</>
                  ) : (
                    <><Database className="mr-2 h-4 w-4" /> Preview Import</>
                  )}
                </Button>
              </>
            ) : importPreview && !showImportWarning ? (
              /* Step 2 footer: Back + Proceed */
              <>
                <Button variant="outline" onClick={() => setImportPreview(null)}>
                  Go Back
                </Button>
                <Button
                  onClick={() => setShowImportWarning(true)}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Upload className="mr-2 h-4 w-4" /> Proceed to Import
                </Button>
              </>
            ) : (
              /* Step 3 footer: Back + Confirm */
              <>
                <Button variant="outline" onClick={() => setShowImportWarning(false)} disabled={importingAll}>
                  Go Back
                </Button>
                <Button
                  onClick={handleImportAllData}
                  disabled={importingAll}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {importingAll ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Importing...</>
                  ) : (
                    <><AlertTriangle className="mr-2 h-4 w-4" /> Yes, Replace All Data</>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export/Import Results Dialog */}
      <Dialog open={exportResultDialogOpen} onOpenChange={setExportResultDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${
              exportResultData?.type === 'import' && exportResultData?.failedCount > 0
                ? 'text-amber-700'
                : exportResultData?.type === 'import'
                  ? 'text-green-700'
                  : 'text-green-700'
            }`}>
              {exportResultData?.type === 'import' ? (
                exportResultData?.failedCount > 0 ? (
                  <><AlertTriangle className="h-5 w-5" /> Import Completed with Errors</>
                ) : (
                  <><FolderUp className="h-5 w-5" /> Import Complete</>
                )
              ) : (
                <><FolderDown className="h-5 w-5" /> Export Complete</>
              )}
            </DialogTitle>
            <DialogDescription>
              {exportResultData?.type === 'import'
                ? exportResultData?.failedCount > 0
                  ? `${exportResultData.failedCount} collection(s) failed to import. See details below.`
                  : 'Database has been restored from backup file.'
                : 'Database backup has been downloaded.'}
            </DialogDescription>
          </DialogHeader>

          {exportResultData && (
            <div className="py-4 space-y-4">
              {/* Summary Stats */}
              <div className={`p-4 rounded-lg ${
                exportResultData.type === 'import' && exportResultData.failedCount > 0
                  ? 'bg-amber-50 border border-amber-200'
                  : exportResultData.type === 'import'
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-green-50 border border-green-200'
              }`}>
                <div className={`grid ${exportResultData.type === 'import' && exportResultData.failedCount > 0 ? 'grid-cols-3' : 'grid-cols-2'} gap-4 text-center`}>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{exportResultData.successCount || exportResultData.totalTables}</p>
                    <p className="text-sm text-slate-600">Tables {exportResultData.type === 'import' ? 'Restored' : 'Exported'}</p>
                  </div>
                  {exportResultData.type === 'import' && exportResultData.failedCount > 0 && (
                    <div>
                      <p className="text-2xl font-bold text-red-600">{exportResultData.failedCount}</p>
                      <p className="text-sm text-red-600">Failed</p>
                    </div>
                  )}
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{(exportResultData.totalRows || 0).toLocaleString()}</p>
                    <p className="text-sm text-slate-600">Total Rows</p>
                  </div>
                </div>
                {exportResultData.filename && (
                  <p className="text-xs text-slate-500 mt-3 text-center truncate">
                    File: {exportResultData.filename}
                  </p>
                )}
              </div>

              {/* Error Summary */}
              {exportResultData.type === 'import' && exportResultData.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                  <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Errors ({exportResultData.errors.length})
                  </h4>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {exportResultData.errors.map((err, idx) => (
                      <p key={idx} className="text-xs text-red-700 break-words">{err}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Tables List */}
              <div>
                <h4 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  {exportResultData.type === 'import' ? 'Restored' : 'Exported'} Tables ({exportResultData.collections?.length})
                </h4>
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-slate-700">Table Name</th>
                        <th className="text-right px-3 py-2 font-medium text-slate-700">Rows</th>
                        {exportResultData.type === 'import' && (
                          <th className="text-center px-3 py-2 font-medium text-slate-700">Status</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {exportResultData.collections?.map((col, idx) => (
                        <tr key={col.name} className={col.success === false ? 'bg-red-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-3 py-2 text-slate-800 font-mono text-xs">
                            {col.name}
                            {col.error && (
                              <p className="text-red-600 font-sans mt-0.5 break-words" title={col.error}>
                                {col.error.length > 80 ? col.error.substring(0, 80) + '...' : col.error}
                              </p>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-slate-600">{(col.count || 0).toLocaleString()}</td>
                          {exportResultData.type === 'import' && (
                            <td className="px-3 py-2 text-center">
                              {col.success !== false ? (
                                <CheckCircle2 className="h-4 w-4 text-green-500 inline" />
                              ) : (
                                <AlertTriangle className="h-4 w-4 text-red-500 inline" />
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {exportResultData.createdAt && (
                <p className="text-xs text-slate-500 text-center">
                  Backup created: {new Date(exportResultData.createdAt).toLocaleString()}
                </p>
              )}
              {exportResultData.backupDate && (
                <p className="text-xs text-slate-500 text-center">
                  Original backup date: {new Date(exportResultData.backupDate).toLocaleString()}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setExportResultDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <FolderDown className="h-5 w-5" />
              Export Database
            </DialogTitle>
            <DialogDescription>
              Export all data as a full backup or download individual tables.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* One-Click Export Section */}
            <div className="p-4 rounded-lg border-2 border-dashed border-green-300 bg-green-50">
              <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                <Database className="h-4 w-4" />
                One-Click Export (Recommended)
              </h4>
              <p className="text-sm text-green-700 mb-3">
                Export all tables as a single backup file for easy one-click import later.
              </p>
              <Button
                onClick={handleExportCombined}
                disabled={exporting}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {exporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Exporting Full Backup...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Export Full Backup
                  </>
                )}
              </Button>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">Or export individual tables</span>
              </div>
            </div>

            <div className="space-y-2">
              {AVAILABLE_COLLECTIONS.map((col) => {
                const Icon = col.icon || Database;
                return (
                  <div
                    key={col.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-4 w-4 text-slate-500" />
                      <div>
                        <p className="font-medium text-sm">{col.name}</p>
                        {col.count != null && <span className="text-xs text-slate-400">{col.count.toLocaleString()} docs</span>}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleExportTable(col.id)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>

            {/* Export all as separate files button */}
            <Button
              variant="outline"
              onClick={handleExportAll}
              disabled={exporting}
              className="w-full"
            >
              {exporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Export All as Separate Files
                </>
              )}
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-700">
              <FolderUp className="h-5 w-5" />
              Import Database
            </DialogTitle>
            <DialogDescription>
              Import from a full backup file (one-click) or select individual table files.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {!importResult ? (
              <>
                <Alert className="bg-red-50 border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 text-sm">
                    <strong>Warning:</strong> Importing will DELETE all existing data in the selected tables
                    and replace with data from the backup files.
                  </AlertDescription>
                </Alert>

                {/* One-Click Import Section */}
                <div className="p-4 rounded-lg border-2 border-dashed border-green-300 bg-green-50">
                  <h4 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    One-Click Import (Recommended)
                  </h4>
                  <p className="text-sm text-green-700 mb-3">
                    Select a backup folder or file to import all tables at once.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Folder Selection Button */}
                    {'showDirectoryPicker' in window && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSelectFolder}
                        className="border-green-400 text-green-700 hover:bg-green-100"
                      >
                        <FolderOpen className="mr-2 h-4 w-4" />
                        Select Folder
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => combinedFileInputRef.current?.click()}
                      className="border-green-400 text-green-700 hover:bg-green-100"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Select Backup File
                    </Button>
                    <input
                      type="file"
                      ref={combinedFileInputRef}
                      className="hidden"
                      accept=".json"
                      onChange={handleCombinedFileSelect}
                    />
                    {combinedBackupFile && (
                      <>
                        <span className="text-sm text-green-700 flex items-center gap-1">
                          <File className="h-3 w-3" />
                          {combinedBackupFile.name}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCombinedBackupFile(null);
                            setSelectedFolderName("");
                            setFolderFiles([]);
                            if (combinedFileInputRef.current) combinedFileInputRef.current.value = '';
                          }}
                          className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Show selected folder info */}
                  {selectedFolderName && (
                    <div className="mt-2 p-2 bg-green-100 rounded text-sm">
                      <p className="flex items-center gap-1 text-green-800">
                        <FolderOpen className="h-3 w-3" />
                        <strong>Folder:</strong> {selectedFolderName}
                      </p>
                      {folderFiles.length > 0 && (
                        <p className="text-green-700 text-xs mt-1">
                          Found {folderFiles.length} backup file(s)
                        </p>
                      )}
                    </div>
                  )}

                  {combinedBackupFile && (
                    <Button
                      onClick={handleImportCombined}
                      disabled={importing}
                      className="mt-3 w-full bg-green-600 hover:bg-green-700"
                    >
                      {importing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Importing All Tables...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Import All Tables Now
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-500">Or import individual tables</span>
                  </div>
                </div>

                <div className="space-y-2">
                  {AVAILABLE_COLLECTIONS.map((col) => {
                    const Icon = col.icon;
                    const selectedFile = selectedImportFiles[col.id];
                    return (
                      <div
                        key={col.id}
                        className={`p-3 rounded-lg border transition-colors ${
                          selectedFile ? 'bg-orange-50 border-orange-300' : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Icon className="h-4 w-4 text-slate-500" />
                            <div>
                              <p className="font-medium text-sm">{col.name}</p>
                              {selectedFile ? (
                                <p className="text-xs text-orange-600 flex items-center gap-1">
                                  <File className="h-3 w-3" />
                                  {selectedFile.name}
                                </p>
                              ) : (
                                <p className="text-xs text-slate-500">{col.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {selectedFile && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => clearSelectedFile(col.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fileInputRefs[col.id]?.current?.click()}
                            >
                              <Upload className="h-4 w-4" />
                            </Button>
                            <input
                              type="file"
                              ref={fileInputRefs[col.id]}
                              accept=".json"
                              onChange={(e) => handleFileSelect(col.id, e)}
                              className="hidden"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="text-sm text-slate-500 text-center">
                  {Object.keys(selectedImportFiles).length} of {AVAILABLE_COLLECTIONS.length} files selected
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <Alert className={Object.values(importResult).every(r => r.success)
                  ? "bg-green-50 border-green-200"
                  : "bg-amber-50 border-amber-200"
                }>
                  <CheckCircle2 className={`h-4 w-4 ${
                    Object.values(importResult).every(r => r.success) ? 'text-green-600' : 'text-amber-600'
                  }`} />
                  <AlertDescription>
                    <strong>Import {Object.values(importResult).every(r => r.success) ? 'Completed' : 'Completed with Errors'}</strong>
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  {Object.entries(importResult).map(([collection, result]) => (
                    <div
                      key={collection}
                      className={`p-3 rounded-lg border ${
                        result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm capitalize">{collection.replace('_', ' ')}</span>
                        {result.success ? (
                          <span className="text-sm text-green-700">
                            <CheckCircle2 className="h-4 w-4 inline mr-1" />
                            {result.count} records imported
                          </span>
                        ) : (
                          <span className="text-sm text-red-700">
                            <AlertTriangle className="h-4 w-4 inline mr-1" />
                            {result.error}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              {importResult ? "Close" : "Cancel"}
            </Button>
            {!importResult && (
              <Button
                variant="destructive"
                onClick={handleImportFiles}
                disabled={importing || Object.keys(selectedImportFiles).length === 0}
              >
                {importing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Import Selected Files
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CRM Export Dialog */}
      <Dialog open={crmExportDialogOpen} onOpenChange={setCrmExportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              <Briefcase className="h-5 w-5" />
              Export CRM Data
            </DialogTitle>
            <DialogDescription>
              Export all CRM related data as a single JSON backup file.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="p-4 rounded-lg border bg-slate-50">
              <h4 className="font-semibold mb-2">This export will include:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Companies and their details</li>
                <li>• All leads and contact information</li>
                <li>• Lead notes and comments</li>
                <li>• Tasks and reminders</li>
                <li>• Communications history</li>
                <li>• Email/SMS templates</li>
                <li>• Email account connections</li>
                <li>• Social account connections</li>
              </ul>
            </div>

            <Button
              onClick={handleExportCRM}
              disabled={crmExporting}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              {crmExporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting CRM Data...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Export All CRM Data
                </>
              )}
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCrmExportDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CRM Import Dialog */}
      <Dialog open={crmImportDialogOpen} onOpenChange={setCrmImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-pink-700">
              <Briefcase className="h-5 w-5" />
              Import CRM Data
            </DialogTitle>
            <DialogDescription>
              Restore all CRM data from a previously exported backup file.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {!crmImportResult ? (
              <>
                <Alert className="bg-red-50 border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800 text-sm">
                    <strong>Warning:</strong> Importing will DELETE all existing CRM data
                    and replace with data from the backup file.
                  </AlertDescription>
                </Alert>

                <div className="p-4 rounded-lg border-2 border-dashed border-pink-300 bg-pink-50">
                  <p className="text-sm text-pink-700 mb-3">
                    Select a CRM backup file (crm_full_backup_*.json)
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => crmFileInputRef.current?.click()}
                      className="border-pink-400 text-pink-700 hover:bg-pink-100"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Select Backup File
                    </Button>
                    <input
                      type="file"
                      ref={crmFileInputRef}
                      className="hidden"
                      accept=".json"
                      onChange={(e) => setCrmBackupFile(e.target.files[0])}
                    />
                    {crmBackupFile && (
                      <>
                        <span className="text-sm text-pink-700 flex items-center gap-1">
                          <File className="h-3 w-3" />
                          {crmBackupFile.name}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCrmBackupFile(null);
                            if (crmFileInputRef.current) crmFileInputRef.current.value = '';
                          }}
                          className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </div>

                  {crmBackupFile && (
                    <Button
                      onClick={handleImportCRM}
                      disabled={crmImporting}
                      className="mt-3 w-full bg-pink-600 hover:bg-pink-700"
                    >
                      {crmImporting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Importing CRM Data...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          Import All CRM Data Now
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <Alert className={Object.values(crmImportResult).every(r => r.success)
                  ? "bg-green-50 border-green-200"
                  : "bg-amber-50 border-amber-200"
                }>
                  <CheckCircle2 className={`h-4 w-4 ${
                    Object.values(crmImportResult).every(r => r.success) ? 'text-green-600' : 'text-amber-600'
                  }`} />
                  <AlertDescription>
                    <strong>Import {Object.values(crmImportResult).every(r => r.success) ? 'Completed' : 'Completed with Errors'}</strong>
                  </AlertDescription>
                </Alert>

                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {Object.entries(crmImportResult).map(([collection, result]) => (
                    <div
                      key={collection}
                      className={`p-3 rounded-lg border ${
                        result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm capitalize">{collection.replace(/_/g, ' ')}</span>
                        {result.success ? (
                          <span className="text-sm text-green-700">
                            <CheckCircle2 className="h-4 w-4 inline mr-1" />
                            {result.imported} records
                          </span>
                        ) : (
                          <span className="text-sm text-red-700">
                            <AlertTriangle className="h-4 w-4 inline mr-1" />
                            {result.error}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCrmImportDialogOpen(false)}>
              {crmImportResult ? "Close" : "Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import FORMS Dialog */}
      <Dialog open={importFormsDialogOpen} onOpenChange={setImportFormsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-cyan-600" />
              Import FORMS
            </DialogTitle>
            <DialogDescription>
              Upload a forms export JSON file to import/update forms
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label htmlFor="forms-import-file">Select Forms JSON File</Label>
              <Input
                id="forms-import-file"
                type="file"
                accept=".json"
                onChange={(e) => setImportFormsFile(e.target.files?.[0] || null)}
              />
              {importFormsFile && (
                <div className="bg-cyan-50 border border-cyan-200 p-3 rounded-lg">
                  <p className="text-sm text-cyan-700">
                    <strong>Selected file:</strong> {importFormsFile.name}
                  </p>
                </div>
              )}
            </div>

            {importFormsResult && (
              <div className={`p-3 rounded-lg border ${importFormsResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <p className={`text-sm font-medium ${importFormsResult.error ? 'text-red-800' : 'text-green-800'}`}>
                  {importFormsResult.message || importFormsResult.error}
                </p>
                {importFormsResult.results && (
                  <div className="mt-2 text-xs text-slate-600">
                    <p>Created: {importFormsResult.results.created}</p>
                    <p>Updated: {importFormsResult.results.updated}</p>
                    <p>Skipped: {importFormsResult.results.skipped}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportFormsDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={handleImportFormsData}
              disabled={!importFormsFile || importingForms}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {importingForms ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Importing...</>
              ) : (
                <><Upload className="mr-2 h-4 w-4" /> Import Forms</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selective Export Dialog */}
      <Dialog open={selectiveExportDialogOpen} onOpenChange={setSelectiveExportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-700">
              <Download className="h-5 w-5" />
              Export Selected Collections
            </DialogTitle>
            <DialogDescription>
              Select the collections you want to export. All data from selected collections will be included.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={selectAllCollections}>
                Select All
              </Button>
              <Button size="sm" variant="outline" onClick={deselectAllCollections}>
                Deselect All
              </Button>
              <Badge variant="outline" className="ml-auto">
                {selectedCollections.length} selected
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {AVAILABLE_COLLECTIONS.map((collection) => (
                <div
                  key={collection.id}
                  className={`p-3 border rounded-lg cursor-pointer transition-all ${
                    selectedCollections.includes(collection.id)
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-slate-200 hover:border-teal-300'
                  }`}
                  onClick={() => toggleCollection(collection.id)}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedCollections.includes(collection.id)}
                      className="pointer-events-none"
                    />
                    {collection.icon ? <collection.icon className="h-4 w-4 text-slate-600" /> : <Database className="h-4 w-4 text-slate-600" />}
                    <span className="font-medium text-sm">{collection.name}</span>
                    {collection.count != null && <span className="text-xs text-slate-400 ml-auto">{collection.count.toLocaleString()}</span>}
                  </div>
                  {collection.description && <p className="text-xs text-slate-500 mt-1 ml-6">{collection.description}</p>}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectiveExportDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSelectiveExport}
              disabled={exportingSelected || selectedCollections.length === 0}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {exportingSelected ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Export {selectedCollections.length} Collection{selectedCollections.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selective Import Dialog */}
      <Dialog open={selectiveImportDialogOpen} onOpenChange={setSelectiveImportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-700">
              <Upload className="h-5 w-5" />
              Import Selected Collections
            </DialogTitle>
            <DialogDescription>
              Upload a backup file and import selected collections. This will REPLACE existing data in the selected collections.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                <strong>Warning:</strong> Importing will REPLACE all existing data in the selected collections. This action cannot be undone.
              </p>
            </div>

            <div>
              <Label>Upload Backup File</Label>
              <div className="mt-2">
                <input
                  ref={selectiveImportFileRef}
                  type="file"
                  accept=".json"
                  onChange={(e) => setSelectiveImportFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => selectiveImportFileRef.current?.click()}
                  className="w-full"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {selectiveImportFile ? selectiveImportFile.name : 'Choose JSON File'}
                </Button>
              </div>
            </div>

            {selectiveImportFile && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={selectAllCollections}>
                    Select All
                  </Button>
                  <Button size="sm" variant="outline" onClick={deselectAllCollections}>
                    Deselect All
                  </Button>
                  <Badge variant="outline" className="ml-auto">
                    {selectedCollections.length} selected
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {AVAILABLE_COLLECTIONS.map((collection) => (
                    <div
                      key={collection.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedCollections.includes(collection.id)
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-slate-200 hover:border-teal-300'
                      }`}
                      onClick={() => toggleCollection(collection.id)}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedCollections.includes(collection.id)}
                          onCheckedChange={() => toggleCollection(collection.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <collection.icon className="h-4 w-4 text-slate-600" />
                        <span className="font-medium text-sm">{collection.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectiveImportResult && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">Import Results:</h4>
                <div className="space-y-1 text-sm text-green-800">
                  {Object.entries(selectiveImportResult).map(([collection, result]) => (
                    <p key={collection}>
                      {collection}: {result.success ? `\u2713 ${result.imported || 0} imported` : `\u2717 ${result.error}`}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setSelectiveImportDialogOpen(false);
              setSelectiveImportFile(null);
              setSelectiveImportResult(null);
              setSelectedCollections([]);
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleSelectiveImport}
              disabled={importingSelected || !selectiveImportFile || selectedCollections.length === 0}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {importingSelected ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Import {selectedCollections.length} Collection{selectedCollections.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync Database Dialog */}
      <Dialog open={syncDatabaseDialogOpen} onOpenChange={setSyncDatabaseDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <RefreshCw className="h-5 w-5" />
              Sync Database to Another Server
            </DialogTitle>
            <DialogDescription>
              Sync your current database to another MongoDB server. Choose merge mode (preserve existing data) or override mode (replace all data).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Connection Details */}
            <div className="space-y-3">
              <div>
                <Label>Target MongoDB Connection String *</Label>
                <Input
                  type="password"
                  placeholder="mongodb://username:password@host:port or mongodb+srv://..."
                  value={syncConnectionString}
                  onChange={(e) => setSyncConnectionString(e.target.value)}
                  className="font-mono text-sm"
                />
              </div>
              <div>
                <Label>Target Database Name *</Label>
                <Input
                  placeholder="dailylog_production"
                  value={syncDatabaseName}
                  onChange={(e) => setSyncDatabaseName(e.target.value)}
                />
              </div>

              {/* Test Connection Button */}
              <div className="flex gap-2">
                <Button
                  onClick={handleTestSyncConnection}
                  disabled={testingSyncConnection || !syncConnectionString || !syncDatabaseName}
                  variant="outline"
                  className="flex-1"
                >
                  {testingSyncConnection ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Cable className="mr-2 h-4 w-4" />
                      Test Connection
                    </>
                  )}
                </Button>
              </div>

              {syncConnectionTestResult && (
                <div className={`p-3 rounded-lg border ${syncConnectionTestResult.success ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                  <p className={`text-sm font-semibold ${syncConnectionTestResult.success ? 'text-green-900' : 'text-red-900'}`}>
                    {syncConnectionTestResult.success ? '\u2713 Connection Successful' : '\u2717 Connection Failed'}
                  </p>
                  <p className="text-xs mt-1 text-slate-600">{syncConnectionTestResult.message}</p>
                  {syncConnectionTestResult.database_info && (
                    <div className="mt-2 text-xs text-slate-600">
                      <p>Server: {syncConnectionTestResult.database_info.server_version || 'N/A'}</p>
                      <p>Collections: {syncConnectionTestResult.database_info.collection_count || 0}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sync Mode Selection */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="font-semibold">Sync Mode</Label>

              <div className="space-y-2">
                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${syncMode === 'merge' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}
                  onClick={() => setSyncMode('merge')}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={syncMode === 'merge'}
                      onCheckedChange={() => setSyncMode('merge')}
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-sm">Merge Sync (Safe)</p>
                      <p className="text-xs text-slate-600 mt-1">
                        Add new fields and tables without overriding existing data. Existing records are preserved.
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${syncMode === 'override' ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-red-300'}`}
                  onClick={() => setSyncMode('override')}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={syncMode === 'override'}
                      onCheckedChange={() => setSyncMode('override')}
                    />
                    <div className="flex-1">
                      <p className="font-semibold text-sm text-red-700">Override Sync (Replace All)</p>
                      <p className="text-xs text-slate-600 mt-1">
                        Replace all data in target database with data from this database. &#x26A0;&#xFE0F; This will DELETE existing data in target.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {syncResult && (
              <div className="bg-green-50 border border-green-300 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">Sync Complete!</h4>
                <div className="space-y-1 text-sm text-green-800">
                  <p>{"\u2713"} Collections synced: {syncResult.collections_synced}</p>
                  <p>{"\u2713"} Total records: {syncResult.total_records}</p>
                  <p>{"\u2713"} Mode: {syncResult.mode === 'merge' ? 'Merge (Safe)' : 'Override (Replace)'}</p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setSyncDatabaseDialogOpen(false);
              setSyncConnectionTestResult(null);
              setSyncResult(null);
            }}>
              Close
            </Button>
            <Button
              onClick={handleSyncDatabase}
              disabled={syncingDatabase || !syncConnectionTestResult?.success}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {syncingDatabase ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Sync Database
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});

export default BackupRestoreCard;
